import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";

function renderContent(content, type) {
  if (type === "poem") {
    return (
      <div className="prose-poem" data-testid="post-content-poem">
        {content}
      </div>
    );
  }
  const paragraphs = content.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className="prose-literary" data-testid="post-content-story">
      {paragraphs.map((p, i) => (
        <p key={`${i}-${p.slice(0, 24)}`}>{p}</p>
      ))}
    </div>
  );
}

export default function PostDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/posts/${slug}`);
        setPost(data);
      } catch (e) {
        setError("This piece could not be found.");
      }
    };
    load();
  }, [slug]);

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="post-error">
        <p className="font-display italic text-2xl text-[#5C554E]">{error}</p>
        <Link to="/" className="btn-link mt-6 inline-block" data-testid="post-back-home">
          ← Back to the Reading Room
        </Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <p className="font-display italic text-[#5C554E]">Turning the page…</p>
      </div>
    );
  }

  return (
    <article className="paper-grain" data-testid="post-detail">
      <div className="max-w-2xl mx-auto px-6 pt-16 md:pt-24 pb-8">
        <p className="overline" data-testid="post-overline">
          {post.type === "poem" ? "Poem" : "Story"} · {post.reading_minutes} min read
        </p>
        <h1
          className={`font-display tracking-tight text-[#1A1A1A] mt-4 ${
            post.type === "poem" ? "text-center text-4xl md:text-5xl" : "text-4xl md:text-6xl leading-[1.05]"
          }`}
          data-testid="post-title"
        >
          {post.title}
        </h1>
        {post.published_at && (
          <p
            className={`overline mt-4 ${post.type === "poem" ? "text-center" : ""}`}
            data-testid="post-date"
          >
            {new Date(post.published_at).toLocaleDateString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        )}
        <div className="ornament" data-testid="post-ornament">
          ❦
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 pb-12">
        {renderContent(post.content, post.type)}
      </div>

      {post.tags?.length > 0 && (
        <div className="max-w-2xl mx-auto px-6 pb-12 flex flex-wrap gap-2 justify-center border-t border-[#D9D2C7] pt-8">
          {post.tags.map((t) => (
            <Link
              key={t}
              to={`/?tag=${encodeURIComponent(t)}`}
              className="tag-pill"
              data-testid={`post-detail-tag-${t}`}
            >
              {t}
            </Link>
          ))}
        </div>
      )}

      <div className="max-w-2xl mx-auto px-6 pb-24 text-center">
        <Link to="/" className="btn-ghost" data-testid="post-back-link">
          Return to the Library
        </Link>
      </div>
    </article>
  );
}
